import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import CheckIcon from '@mui/icons-material/Check';
import JsonViewer from 'shared/JsonViewer';

import {
  Checkbox,
  TableBody,
  TableCell,
  Tooltip,
  Button,
  TableRow,
  Table as MuiTable,
  TableContainer as MuiTableContainer,
  Collapse,
} from '@mui/material';

import { falsyCheckDate } from 'utils';
import { selectTeamHash } from 'selectors';
import {
  ErrorInterface,
  RecentErrorsType,
  FilterStateInterface,
  FilterProps,
  Item,
  NetworkErrorsLogType,
  EntityErrorPageUrlQueryParams,
  HeadCell,
} from 'types';

import TableHead from 'components/TableHead';
import EmptyTableContent from 'components/EmptyTableContent';
import { LinearBuffer } from 'components/Progress';
import Table from 'components/Table';
import CollapseCell from 'components/CollapseCell';
import SingleEntityModal from 'components/SingleEntityModal';
import useAppNavigation from 'hooks/useAppNavigation';
import { fetchSingleEntity } from 'thunk';
import * as constants from '@constants';
import SingleValuePicker from 'components/SingleValuePicker';
import useBulkActionsSelector from 'hooks/useBulkActionsSelector';
import EntityIdOptionSelector from 'shared/EntityIdOptionSelector';
import usePendingEntityStatusModal from 'hooks/usePendingEntityStatusModal';

type CustomRowProps = {
  navigateToEntityErrorsTable: (
    args: {
      openInNewTab: boolean;
    } & EntityErrorPageUrlQueryParams,
  ) => void;
  onSingleEntityModalOpen: (entityId: string) => () => void;
  openPendingEntityStatusModal: ReturnType<
    typeof usePendingEntityStatusModal
  >['openPendingEntityStatusModal'];
};

type NetWorkErrorsLogRowProps = NetworkErrorsLogType & CustomRowProps;

const IntegrationServerEntityIdCell = (row: NetWorkErrorsLogRowProps) => {
  const { entityId, targetServiceId, onSingleEntityModalOpen, openPendingEntityStatusModal } = row;

  return (
    <EntityIdOptionSelector
      entityId={+entityId}
      targetServiceId={targetServiceId}
      onSingleEntityModalOpen={onSingleEntityModalOpen(entityId)}
      openPendingEntityStatusModal={openPendingEntityStatusModal}
    />
  );
};

const Details = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 500px;
`;

const Styler = styled.div`
  /* Error log table container */
`;

const defaultHeadCells = [
  {
    id: 'mosaic_team_id',
    label: 'Team ID',
  },
  {
    id: 'target_service_id',
    label: 'Integration ID',
  },
  {
    id: 'mosaic_team_name',
    label: 'Team',
  },
  {
    id: 'created_at',
    label: 'Created At',
  },
  {
    id: 'updated_at',
    label: 'Updated At',
  },
  {
    id: 'data_type',
    label: 'Data Type',
  },
  {
    id: 'response_time',
    label: 'Response Time',
  },
  {
    id: 'response',
    label: 'Response',
  },
  {
    id: 'message',
    label: 'Message',
  },
];
const noop = () => {};

const getJsonExtractedValue = (valueToSeek: string, json: string): string | null => {
  let extractedValue: string | null = null;
  const valueToSeekIndex = json.indexOf(valueToSeek);
  if (valueToSeekIndex > 0) {
    const beginOfStringToSeek = json.slice(valueToSeekIndex + valueToSeek.length + 3);
    const endOfValueToSeek = beginOfStringToSeek.indexOf('"');
    extractedValue = beginOfStringToSeek.slice(0, endOfValueToSeek);
  }

  return extractedValue;
};

const LinkText = styled.span`
  cursor: pointer;
  text-decoration: underline;
  color: blue;
`;

const NO_ERRORS_MESSAGE = 'No errors were found';
const FETCH_ERROR_MESSAGE = 'Unable to load error logs. Please verify your filters and try again.';
const DEFAULT_ROW_PER_PAGE = 100;

type ErrorLogTableProps = {
  targetService: string;
  filterState: FilterStateInterface;
  error: ErrorInterface;
  isLoading: boolean;
  data: Array<RecentErrorsType> | NetWorkErrorsLogRowProps;
  onPageChange: any;
  onRequestSort: any;
  total: number;
  errorType: Item;
  onAllCollapse: () => void;
  collapseProps: {
    isAllCollapse: boolean;
    isFirstTimeAllCollapse: boolean;
    setIsAllCollapse: (errorType: string, value: boolean) => void;
    setIsFirstTimeAllCollapse: (errorType: string, value: boolean) => void;
  };
  onFetchNew: () => void;
  tableTitle?: string | JSX.Element;
  headCells?: HeadCell[];
  filtersToShow?: FilterProps | any;
  teamId?: number;
};

// <CollapseCell
// disabled={!errorType.value}
// isExpanded={isAllCollapse}
// onCollapse={toggleCollapseAll}
// useDefaultTextCollapseButton
// />

// Keep the Search button in normal document flow so it doesn't
// overlap filter controls rendered above the table header.
const StyledSearchButton = styled(Button)`
  position: static;
  margin-top: 8px;
`;
/** Do not show meta data column, show formatted in expansion */
/** When click on sessionId or requestId, just pop into another page */
const ErrorLogTable = ({
  headCells = defaultHeadCells,
  teamId,
  filterState,
  error,
  isLoading,
  data,
  onPageChange,
  onRequestSort,
  tableTitle,
  onAllCollapse,
  total,
  filtersToShow,
  collapseProps,
  targetService,
  errorType,
  onFetchNew,
}: ErrorLogTableProps) => {
  const {
    navigateToIntegrationDetailsPage,
    navigateToNetworkLog,
    navigateToTeamProfile,
    navigateToEntityErrorsTable,
  } = useAppNavigation();

  const teamHash = useSelector(selectTeamHash);
  const dispatch = useDispatch();

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [singleEntityModalOpen, setSingleEntityModalOpen] = useState(false);
  const { openPendingEntityStatusModal } = usePendingEntityStatusModal();

  const { isAllCollapse, isFirstTimeAllCollapse, setIsAllCollapse, setIsFirstTimeAllCollapse } =
    collapseProps;

  const onSingleEntityModalClose = () => {
    setSingleEntityModalOpen(false);
  };
  const onSingleEntityModalOpen = (entityId: number) => () => {
    // TODO: Fix with Redux Toolkit configureStore migration
    dispatch(
      fetchSingleEntity({
        entityId,
      }) as any,
    );
    setSingleEntityModalOpen(true);
  };

  const toggleCollapseAll = () => {
    onAllCollapse();
    setIsAllCollapse(errorType.value as string, !isAllCollapse);
    if (isFirstTimeAllCollapse) {
      setIsFirstTimeAllCollapse(errorType.value as string, false);
    }
  };

  const onSearch = () => onFetchNew();

  const formattedHeadCells = [
    {
      id: 'collapse',
      label: '',
      disableSort: true,
      style: { width: 56 },
    },
    ...headCells,
  ];

  // Inject a Search action below the filters bar
  const filtersWithActions = filtersToShow
    ? {
        ...filtersToShow,
        filtersProps: {
          ...(filtersToShow?.filtersProps || {}),
          actions: (
            <StyledSearchButton color="primary" onClick={onSearch} disabled={!errorType.value}>
              Search
            </StyledSearchButton>
          ),
        },
      }
    : filtersToShow;

  const onCollapse = (id: string) => () => {
    setExpandedRows({
      ...expandedRows,
      [id]: !expandedRows[id],
    });
  };

  /** Key by startCase(headCell.id), to normalize  */
  const renderCellHash = {
    [constants.AGENT]: {
      created_at: (row: RecentErrorsType) => falsyCheckDate(row.created_at),
      updated_at: (row: RecentErrorsType) => falsyCheckDate(row.updated_at),
      mosaic_team_name: (row: RecentErrorsType) => {
        return row.mosaic_team_name || teamHash[row.mosaic_team_id || teamId || 0]?.name || 'N/A';
      },
      mosaic_team_id: (row: RecentErrorsType) => {
        const mosaicTeamId = row.mosaic_team_id;
        if (mosaicTeamId) {
          return (
            <LinkText onClick={() => navigateToTeamProfile({ teamId: mosaicTeamId })}>
              {mosaicTeamId}
            </LinkText>
          );
        }
        return <div>{'N/A'}</div>;
      },
      data_type: (row: RecentErrorsType) => {
        const dataType = row.data_type;
        return <div>{dataType || 'N/A'}</div>;
      },
      message: (row: RecentErrorsType) => (
        <Details>{row.message || row.details || row.error_code}</Details>
      ),
      collapse: (row: RecentErrorsType) => {
        const id = row.created_at;
        return <CollapseCell isExpanded={expandedRows[id]} onCollapse={onCollapse(id)} />;
      },
      target_service_id: (row: RecentErrorsType) => {
        const { target_service_id, mosaic_team_id } = row;

        return (
          <LinkText
            onClick={navigateToIntegrationDetailsPage(
              mosaic_team_id,
              targetService,
              target_service_id,
            )}
          >
            {target_service_id}
          </LinkText>
        );
      },
      id: (row: RecentErrorsType) => {
        const { id } = row;
        return <p>{id}</p>;
      },
      session_id: (row: RecentErrorsType) => {
        const { mosaic_team_id, session_id } = row;
        return (
          <LinkText onClick={navigateToNetworkLog(mosaic_team_id, null, session_id)}>
            {session_id}
          </LinkText>
        );
      },
    },
    [constants.NETWORK]: {
      createdAt: (row: NetWorkErrorsLogRowProps) => falsyCheckDate(row.createdAt),
      updatedAt: (row: NetWorkErrorsLogRowProps) => falsyCheckDate(row.updatedAt),
      mosaicTeamId: (row: NetWorkErrorsLogRowProps) => {
        const mosaicTeamId = row.mosaicTeamId;
        if (mosaicTeamId) {
          return (
            <LinkText onClick={() => navigateToTeamProfile({ teamId: mosaicTeamId })}>
              {mosaicTeamId}
            </LinkText>
          );
        }
        return <div>{'N/A'}</div>;
      },
      dataType: (row: NetWorkErrorsLogRowProps) => {
        const { dataType } = row;
        return <div>{dataType || 'N/A'}</div>;
      },
      collapse: (row: NetWorkErrorsLogRowProps) => {
        /**  */
        const id = row.createdAt;
        return <CollapseCell isExpanded={expandedRows[id]} onCollapse={onCollapse(id)} />;
      },
      targetServiceId: (row: NetWorkErrorsLogRowProps) => {
        const { targetServiceId, mosaicTeamId } = row;

        return (
          <LinkText
            onClick={navigateToIntegrationDetailsPage(mosaicTeamId, targetService, targetServiceId)}
          >
            {targetServiceId}
          </LinkText>
        );
      },
      sessionId: (row: NetWorkErrorsLogRowProps) => {
        const { mosaicTeamId, sessionId, apiRequestId } = row;
        return (
          <LinkText onClick={navigateToNetworkLog(parseInt(mosaicTeamId), apiRequestId, sessionId)}>
            {sessionId}
          </LinkText>
        );
      },
      apiRequestId: (row: NetWorkErrorsLogRowProps) => {
        const { apiRequestId, sessionId, mosaicTeamId } = row;
        return (
          <LinkText onClick={navigateToNetworkLog(parseInt(mosaicTeamId), apiRequestId, sessionId)}>
            {apiRequestId}
          </LinkText>
        );
      },
      entityId: IntegrationServerEntityIdCell,
      id: (row: NetWorkErrorsLogRowProps) => {
        const { id } = row;
        return <p>{id}</p>;
      },
      error: (row: NetWorkErrorsLogRowProps) => {
        const { error } = row;
        return <p>{error}</p>;
      },
    },
  };

  const renderCell = (
    row: RecentErrorsType | NetWorkErrorsLogRowProps | any,
    headCellId: string,
  ) => {
    const cellRendererHash = renderCellHash[errorType.value as keyof typeof renderCellHash];

    const cellRenderer = cellRendererHash[headCellId as keyof typeof cellRendererHash];
    return cellRenderer
      ? cellRenderer({
          ...row,
          navigateToEntityErrorsTable,
          onSingleEntityModalOpen,
          openPendingEntityStatusModal,
        })
      : '';
  };

  const renderRowsHash = {
    [constants.AGENT]: () => {
      return (data as RecentErrorsType[]).map((row: RecentErrorsType) => {
        const headCellKeys = formattedHeadCells.map((headCell) => headCell.id);

        const id = row.created_at;

        const keysToNotShow = new Set([...headCellKeys, 'total_rows']);
        return (
          <>
            <TableRow
              key={`${id}-main`}
              hover
              style={{ cursor: 'pointer' }}
              onClick={onCollapse(id)}
              role="checkbox"
              tabIndex={-1}
            >
              {formattedHeadCells.map((headCell) => (
                <TableCell key={headCell.id}>{renderCell(row, headCell.id)}</TableCell>
              ))}
            </TableRow>
            <TableRow key={`${id}-details`}>
              <TableCell
                style={{ paddingBottom: 0, paddingTop: 0 }}
                colSpan={formattedHeadCells.length}
              >
                <Collapse in={expandedRows[id]} timeout="auto" unmountOnExit>
                  <h4>
                    Origin: <span>{row.origin}</span>
                  </h4>

                  <h4>
                    Target: <span>{row.target}</span>
                  </h4>

                  <JsonViewer name={false} src={row} collapseStringsAfterLength={150} />
                  {/* {Object.keys(row).map((key: string) =>
                    keysToNotShow.has(key) ||
                    !row[key as keyof NetWorkErrorsLogRowProps] ? null : (
                      <div style={{ padding: "10px 20px" }}>
                        <div>
                          <b>{startCase(key)}:</b>{" "}
                          {renderSubRowRecursive(
                            row[key as keyof NetWorkErrorsLogRowProps] as any
                          )}
                        </div>
                      </div>
                    )
                  )} */}
                </Collapse>
              </TableCell>
            </TableRow>
          </>
        );
      });
    },
    [constants.NETWORK]: () => {
      return (data as unknown as NetworkErrorsLogType[]).map((row: NetworkErrorsLogType) => {
        const headCellKeys = formattedHeadCells.map((headCell) => headCell.id);
        const keysToNotShow = new Set([...headCellKeys, 'metadata', 'total_rows']);
        const id = row.createdAt;

        return (
          <>
            <TableRow
              key={`${id}-main`}
              hover
              style={{ cursor: 'pointer' }}
              onClick={onCollapse(id)}
              role="checkbox"
              tabIndex={-1}
            >
              {formattedHeadCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  onClick={
                    'shouldStopPropagationOnClick' in headCell &&
                    headCell.shouldStopPropagationOnClick
                      ? (e) => {
                          e.stopPropagation();
                        }
                      : noop
                  }
                >
                  {renderCell(row, headCell.id)}
                </TableCell>
              ))}
            </TableRow>
            <TableRow key={`${id}-details`}>
              <TableCell
                style={{ paddingBottom: 0, paddingTop: 0 }}
                colSpan={formattedHeadCells.length}
              >
                <Collapse in={expandedRows[id]} timeout="auto" unmountOnExit>
                  <JsonViewer name={false} src={row} collapseStringsAfterLength={150} />
                  {/* {Object.keys(row).map((key: string) =>
                        keysToNotShow.has(key) ||
                        !row[key as keyof NetWorkErrorsLogRowProps] ? null : (
                          <div style={{ padding: "10px 20px" }}>
                            <div>
                              <b>{startCase(key)}:</b>{" "}
                              {renderSubRowRecursive(
                                row[key as keyof NetWorkErrorsLogRowProps] as any
                              )}
                            </div>
                          </div>
                        )
                      )} */}
                  {/* {Object.keys(row).map((key: string) =>
                      keysToNotShow.has(key) ||
                      !row[key as keyof NetWorkErrorsLogRowProps] ? null : (
                        <div style={{ padding: "10px 20px" }}>
                          <div>
                            <b>{startCase(key)}:</b>{" "}
                            {renderSubRowRecursive(
                              row[key as keyof NetWorkErrorsLogRowProps] as any
                            )}
                          </div>
                        </div>
                      )
                    )} */}
                </Collapse>
              </TableCell>
            </TableRow>
          </>
        );
      });
    },
  };

  const renderTableHead = () => (
    <TableHead
      numSelected={0}
      onSelectAllClick={noop}
      orderDirection={filterState?.orderDirection as 'desc' | 'asc'}
      orderBy={filterState?.orderBy as string}
      onRequestSort={onRequestSort}
      rowCount={total}
      headCells={formattedHeadCells}
    />
  );

  const renderTableBody = () => {
    const renderRowsFn = renderRowsHash[errorType.value as keyof typeof renderRowsHash];
    if (total > 0) return <TableBody>{renderRowsFn()}</TableBody>;
  };

  const renderTable = () => {
    return (
      <>
        {/* GLX-1355: custom renderTable bypasses the default-table scroll
            container in components/Table, so provide one here — otherwise the
            table grows unbounded and the page has no vertical scrollbar. */}
        <MuiTableContainer sx={{ maxHeight: '1000px', overflow: 'auto' }}>
          <MuiTable
            stickyHeader
            sx={{
              // components/TableHead paints header cells transparent (the fill
              // lives on the row), but position:sticky pins the <th>, not the
              // <tr> — so body rows bleed through the pinned header unless the
              // cells get the default-table's opaque backing (components/Table).
              '& .MuiTableHead-root .MuiTableCell-root': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(15, 23, 42, 0.98)'
                    : 'rgba(248, 250, 252, 0.98)',
                backdropFilter: 'blur(12px)',
              },
            }}
          >
            {renderTableHead()}
            {!isLoading && renderTableBody()}
          </MuiTable>
        </MuiTableContainer>
        {!errorType.value && !isLoading && (
          <EmptyTableContent message="Please select a Log Type above to view error logs" />
        )}
        {errorType.value && total === 0 && !error?.message && !isLoading && (
          <EmptyTableContent IconEl={CheckIcon} message={NO_ERRORS_MESSAGE} />
        )}
        {isLoading && <LinearBuffer />}
        {error?.message && !isLoading && <EmptyTableContent message={FETCH_ERROR_MESSAGE} />}
      </>
    );
  };

  return (
    <Styler>
      <Table
        renderTable={renderTable}
        // Toolbar
        filtersToShow={filtersWithActions}
        tableTitle={tableTitle}
        // Pagination
        hidePagination={!errorType.value}
        rowsPerPageOptions={[filterState.limit]}
        rowsPerPage={filterState.limit}
        count={total || 0}
        page={(filterState?.page as number) || 0}
        onPageChange={onPageChange}
      />
      {singleEntityModalOpen && (
        <SingleEntityModal open={singleEntityModalOpen} onClose={onSingleEntityModalClose} />
      )}
    </Styler>
  );
};

export default ErrorLogTable;
